import React, { useState, useMemo } from 'react';

const RoleCard = ({ role }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow mb-3">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2">{role.role}</h3>
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          role.status === 'Open' ? 'bg-green-100 text-green-800' :
          role.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
          role.status === 'Filled' ? 'bg-gray-100 text-gray-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {role.status}
        </span>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span className="truncate">{role.client}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-600">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="truncate">{role.department}</span>
        </div>
      </div>
    </div>
  );
};

const KanbanColumn = ({ title, roles, count, color }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 min-h-[calc(100vh-400px)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 ${color}`}></div>
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        <span className="bg-white text-gray-600 text-sm font-medium px-2 py-1 rounded-full">
          {count}
        </span>
      </div>
      
      <div className="space-y-3">
        {roles.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm">No roles</p>
          </div>
        ) : (
          roles.map(role => (
            <RoleCard key={role.id} role={role} />
          ))
        )}
      </div>
    </div>
  );
};

const OpenRolesKanban = ({ roles }) => {
  const [groupBy, setGroupBy] = useState('department'); // 'department' or 'client'

  // Group roles by the selected criteria
  const groupedRoles = useMemo(() => {
    const groups = {};
    
    roles.forEach(role => {
      const key = groupBy === 'department' ? role.department : role.client;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(role);
    });

    return groups;
  }, [roles, groupBy]);

  // Get sorted group names
  const groupNames = useMemo(() => {
    return Object.keys(groupedRoles).sort();
  }, [groupedRoles]);

  // Color mapping for columns
  const getColumnColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-orange-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-red-500'
    ];
    return colors[index % colors.length];
  };

  if (roles.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <h3 className="text-lg font-medium text-gray-800 mb-2">No roles match your filters</h3>
        <p className="text-gray-500">Try adjusting your search criteria or clear the filters to see all roles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Group By Toggle */}
      <div className="dashboard-card p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Kanban Board</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Group by:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setGroupBy('department')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  groupBy === 'department'
                    ? 'bg-white text-primary-color shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Department
              </button>
              <button
                onClick={() => setGroupBy('client')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  groupBy === 'client'
                    ? 'bg-white text-primary-color shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Client
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="overflow-x-auto">
        <div className="flex gap-6 min-w-max pb-4">
          {groupNames.map((groupName, index) => (
            <div key={groupName} className="w-80 flex-shrink-0">
              <KanbanColumn
                title={groupName || 'Unassigned'}
                roles={groupedRoles[groupName] || []}
                count={groupedRoles[groupName]?.length || 0}
                color={getColumnColor(index)}
              />
            </div>
          ))}
          
          {groupNames.length === 0 && (
            <div className="w-80">
              <KanbanColumn
                title="No Groups"
                roles={[]}
                count={0}
                color="bg-gray-400"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="dashboard-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-primary-color">{roles.length}</div>
            <div className="text-sm text-gray-600">Total Roles</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{groupNames.length}</div>
            <div className="text-sm text-gray-600">{groupBy === 'department' ? 'Departments' : 'Clients'}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {roles.filter(role => role.status === 'Open').length}
            </div>
            <div className="text-sm text-gray-600">Open Positions</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(roles.length / (groupNames.length || 1))}
            </div>
            <div className="text-sm text-gray-600">Avg per {groupBy === 'department' ? 'Dept' : 'Client'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenRolesKanban;